// Picture-in-Picture To-Do List Widget
import React, { useState, useEffect } from 'react';
import { CheckSquare, Plus, X, Minimize2, Maximize2, Trash2, Calendar, Clock, AlertCircle, Edit2 } from 'lucide-react';
import { turso } from '@/database/client';

interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
  createdAt: number;
  userId: string;
  dueDate?: string;
}

// Add keyframe animation for strikethrough effect
const style = document.createElement('style');
style.textContent = `
  @keyframes strikethroughReveal {
    from {
      text-decoration-color: transparent;
    }
    to {
      text-decoration-color: currentColor;
    }
  }
  
  .animate-strikethrough {
    text-decoration: line-through;
    text-decoration-thickness: 1.5px;
    text-decoration-color: transparent;
    animation: strikethroughReveal 0.4s ease-out forwards;
  }

  @keyframes pulse-glow {
    0%, 100% {
      opacity: 1;
      box-shadow: 0 0 4px currentColor;
    }
    50% {
      opacity: 0.6;
      box-shadow: 0 0 8px currentColor;
    }
  }
  
  .pulse-alert {
    animation: pulse-glow 2s ease-in-out infinite;
  }
`;
if (typeof document !== 'undefined' && !document.querySelector('style[data-todo-animation]')) {
  style.setAttribute('data-todo-animation', 'true');
  document.head.appendChild(style);
}

interface TodoListPIPProps {
  isOpen?: boolean;
  onClose?: () => void;
  zIndex?: number;
  onFocus?: () => void;
}

export default function TodoListPIP({ isOpen, onClose, zIndex = 99999, onFocus }: TodoListPIPProps) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMinimized, setIsMinimized] = useState(true); // Start minimized/docked
  const [isVisible, setIsVisible] = useState(true);
  const [isDocked, setIsDocked] = useState(true); // Track if docked or open
  const [position, setPosition] = useState({ x: 20, y: 100 }); // Upper LEFT side of screen
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [newTodoText, setNewTodoText] = useState('');
  const [newTodoDueDate, setNewTodoDueDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [editingDateId, setEditingDateId] = useState<string | null>(null);
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  // Sync with parent isOpen prop
  useEffect(() => {
    if (isOpen !== undefined) {
      if (isOpen) {
        // Parent wants to open - open to center
        setIsDocked(false);
        setIsMinimized(false);
        setPosition({
          x: (window.innerWidth - 300) / 2,
          y: (window.innerHeight - 400) / 2
        });
      } else if (!isOpen) {
        // Parent wants to close - just hide (let parent tab handle visibility)
        setIsDocked(true);
        setIsMinimized(true);
      }
    }
  }, [isOpen]);

  const getCurrentUser = () => {
    const userStr = localStorage.getItem('whipt_user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  };

  const loadTodos = async () => {
    const user = getCurrentUser();
    console.log('🔍 TodoList loadTodos called. User:', user);
    if (!user?.id) {
      console.log('⚠️ No user logged in - skipping todo load');
      setIsLoading(false);
      return;
    }

    try {
      console.log('📥 Loading todos for user:', user.id);
      const result = await turso.execute({
        sql: `SELECT * FROM entities WHERE entity_type = 'Todo' ORDER BY created_at DESC`,
        args: []
      });

      console.log(`📊 Raw todos from DB: ${result.rows.length}`);

      const dbTodos = result.rows
        .map(row => {
          const data = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
          return {
            id: String(row.id),
            text: data.text || '',
            completed: data.completed || false,
            createdAt: data.createdAt || Date.now(),
            userId: String(data.userId || ''),
            dueDate: data.dueDate || undefined
          };
        })
        .filter(todo => {
          const matches = todo.userId === user.id;
          if (!matches) console.log(`❌ Todo "${todo.text.substring(0, 30)}" user ${todo.userId} doesn't match ${user.id}`);
          return matches;
        }); // Filter by userId in JSON data

      console.log(`✅ Loaded ${dbTodos.length} todos from database (filtered from ${result.rows.length} total)`);
      console.log('📋 First 3 todos:', dbTodos.slice(0, 3).map(t => t.text));
      setTodos(dbTodos);
    } catch (error) {
      console.error('❌ Failed to load todos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const savedSettings = localStorage.getItem('whipt_todolist_settings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setPosition(settings.position || position);
      setIsVisible(settings.isVisible !== false);
      setIsMinimized(settings.isMinimized || false);
    }
    loadTodos();
    
    // Reload todos when window gains focus (in case user logged in elsewhere)
    const handleFocus = () => {
      console.log('Window focused - reloading todos');
      loadTodos();
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Reload todos when visibility changes
  useEffect(() => {
    if (isVisible && !isLoading) {
      console.log('Widget became visible - checking for updates');
      loadTodos();
    }
  }, [isVisible, isLoading]);

  // Update badge count for tab display
  useEffect(() => {
    const activeTodos = todos.filter(t => !t.completed);
    localStorage.setItem('whipt_todolist_badge', activeTodos.length.toString());
  }, [todos]);

  useEffect(() => {
    localStorage.setItem('whipt_todolist_settings', JSON.stringify({ position, isVisible, isMinimized }));
  }, [position, isVisible, isMinimized]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        setIsVisible(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging from header area when open/minimized
    const target = e.target as HTMLElement;
    
    // Don't start dragging if clicking on interactive elements
    if (target.closest('button') || target.closest('input') || target.closest('textarea')) return;
    
    // Allow dragging from the header or when minimized
    if (!isDocked) {
      setIsDragging(true);
      setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      
      // Keep within screen bounds
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - (isMinimized ? 200 : 300), newX)),
        y: Math.max(0, Math.min(window.innerHeight - 100, newY))
      });
      
      // If dragged, it's no longer docked
      if (isDocked) {
        setIsDocked(false);
      }
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, isMinimized, isDocked]);

  const addTodo = async () => {
    if (!newTodoText.trim()) return;
    const user = getCurrentUser();
    if (!user?.id) {
      alert('Please sign in to save todos');
      return;
    }

    const newTodo: TodoItem = {
      id: `todo-${Date.now()}`,
      text: newTodoText.trim(),
      completed: false,
      createdAt: Date.now(),
      userId: user.id,
      dueDate: newTodoDueDate || undefined
    };

    // Optimistic update
    setTodos([newTodo, ...todos]);
    setNewTodoText('');
    setNewTodoDueDate('');

    // Save to database
    try {
      console.log('💾 Saving todo to database:', newTodo.id);
      await turso.execute({
        sql: `INSERT INTO entities (entity_type, data, created_at, updated_at) VALUES ('Todo', ?, datetime('now'), datetime('now'))`,
        args: [JSON.stringify({ text: newTodo.text, completed: newTodo.completed, createdAt: newTodo.createdAt, dueDate: newTodo.dueDate, userId: user.id })]
      });
      console.log('✅ Todo saved successfully');
      // Reload to get the correct database ID
      await loadTodos();
    } catch (error) {
      console.error('❌ Failed to save todo:', error);
      alert('Failed to save todo. Please check your connection and try again.');
      // Revert optimistic update on error
      setTodos(todos);
    }
  };

  const toggleTodo = async (id: string) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;

    setTodos(todos.map(t => t.id === id ? { ...t, completed: !t.completed } : t));

    try {
      await turso.execute({
        sql: `UPDATE entities SET data = ?, updated_at = datetime('now') WHERE id = ? AND entity_type = 'Todo'`,
        args: [JSON.stringify({ text: todo.text, completed: !todo.completed, createdAt: todo.createdAt, dueDate: todo.dueDate, userId: todo.userId }), id]
      });
    } catch (error) {
      console.error('Failed to update todo:', error);
    }
  };

  const deleteTodo = async (id: string) => {
    setTodos(todos.filter(todo => todo.id !== id));
    try {
      await turso.execute({
        sql: `DELETE FROM entities WHERE id = ? AND entity_type = 'Todo'`,
        args: [id]
      });
    } catch (error) {
      console.error('Failed to delete todo:', error);
    }
  };

  const updateTodoDueDate = async (id: string, dueDate: string) => {
    const todo = todos.find(t => t.id === id);
    if (!todo) return;

    setTodos(todos.map(t => t.id === id ? { ...t, dueDate } : t));
    setEditingDateId(null);

    try {
      await turso.execute({
        sql: `UPDATE entities SET data = ?, updated_at = datetime('now') WHERE id = ? AND entity_type = 'Todo'`,
        args: [JSON.stringify({ text: todo.text, completed: todo.completed, createdAt: todo.createdAt, dueDate, userId: todo.userId }), id]
      });
    } catch (error) {
      console.error('Failed to update due date:', error);
    }
  };

  const updateTodoText = async (id: string, newText: string) => {
    const todo = todos.find(t => t.id === id);
    if (!todo || !newText.trim()) return;

    setTodos(todos.map(t => t.id === id ? { ...t, text: newText.trim() } : t));
    setEditingTextId(null);

    try {
      await turso.execute({
        sql: `UPDATE entities SET data = ?, updated_at = datetime('now') WHERE id = ? AND entity_type = 'Todo'`,
        args: [JSON.stringify({ text: newText.trim(), completed: todo.completed, createdAt: todo.createdAt, dueDate: todo.dueDate, userId: todo.userId }), id]
      });
    } catch (error) {
      console.error('Failed to update todo text:', error);
    }
  };

  const getDueStatus = (dueDate?: string) => {
    if (!dueDate) return null;
    
    // Parse date as local timezone (not UTC)
    const dueDateTime = new Date(dueDate + 'T00:00:00'); // Treat as start of day in local timezone
    const now = new Date();
    
    // Set both to start of day for accurate comparison
    const dueStartOfDay = new Date(dueDateTime.getFullYear(), dueDateTime.getMonth(), dueDateTime.getDate());
    const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const diffMs = dueStartOfDay.getTime() - nowStartOfDay.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    
    if (diffDays < 0) return { status: 'overdue', color: 'text-red-400 bg-red-500/20', icon: AlertCircle };
    if (diffDays === 0) return { status: 'due-today', color: 'text-amber-400 bg-amber-500/20', icon: Clock };
    if (diffDays < 2) return { status: 'due-soon', color: 'text-amber-400 bg-amber-500/20', icon: Clock };
    return { status: 'upcoming', color: 'text-slate-400 bg-slate-700/30', icon: Calendar };
  };

  const formatDueDate = (dueDate?: string) => {
    if (!dueDate) return '';
    const date = new Date(dueDate + 'T00:00:00'); // Local timezone
    const now = new Date();
    
    // Compare dates at start of day
    const dueStartOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffMs = dueStartOfDay.getTime() - nowStartOfDay.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    if (diffDays < 7) return `${diffDays} days`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const activeTodos = todos.filter(t => !t.completed);
  const completedTodos = todos.filter(t => t.completed);
  
  // Count urgent/overdue items for alert badge
  const urgentCount = activeTodos.filter(todo => {
    const status = getDueStatus(todo.dueDate);
    return status && (status.status === 'overdue' || status.status === 'due-today');
  }).length;

  // Open to center of screen
  const openToCenter = () => {
    setIsDocked(false);
    setIsMinimized(false);
    setPosition({
      x: (window.innerWidth - 300) / 2,
      y: (window.innerHeight - 400) / 2
    });
  };

  // Return to docked position - let parent tab handle visibility
  const returnToDock = () => {
    setIsDocked(true);
    setIsMinimized(true);
    onClose?.(); // Call parent callback to hide and show tab
  };

  // Only render when parent explicitly says to open (must be exactly true)
  if (isOpen !== true) {
    return null;
  }

  // Show floating button when completely hidden (keyboard shortcut recovery)
  if (!isVisible) {
    return (
      <button
        onClick={() => { setIsVisible(true); openToCenter(); }}
        className={`fixed top-20 left-6 z-40 p-3 ${isMobile ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500' : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500'} rounded-full shadow-2xl transition-all hover:scale-110 group`}
        title={`Open To-Do List (${isMobile ? 'Mobile' : 'Desktop'}) - Cmd/Ctrl + Shift + T`}
      >
        <CheckSquare className="w-5 h-5 text-white" />
        {activeTodos.length > 0 && (
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full text-white text-xs font-bold flex items-center justify-center">
            {activeTodos.length}
          </span>
        )}
        {urgentCount > 0 && (
          <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center pulse-alert">
            <AlertCircle className="w-3 h-3 text-white" />
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      onMouseDown={() => onFocus?.()}
      style={{ 
        position: 'fixed', 
        left: `${position.x}px`, 
        top: `${position.y}px`, 
        zIndex: zIndex, 
        width: isMinimized ? '200px' : '300px' 
      }}
      className="select-none shadow-2xl rounded-xl overflow-hidden"
    >
      <div 
        className={`${isMobile ? 'bg-gradient-to-r from-emerald-600 to-teal-600' : 'bg-gradient-to-r from-purple-600 to-indigo-600'} px-3 py-2 flex items-center justify-between cursor-grab active:cursor-grabbing`}
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-white" />
          <span className="text-sm font-semibold text-white">To-Do List (Desktop)</span>
          {activeTodos.length > 0 && !isMinimized && <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full text-white">{activeTodos.length}</span>}
          {isMinimized && urgentCount > 0 && (
            <span className="text-xs bg-red-500 px-2 py-0.5 rounded-full text-white font-bold pulse-alert flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {urgentCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }} className="p-1 hover:bg-white/20 rounded transition-colors">
            {isMinimized ? <Maximize2 className="w-3 h-3 text-white" /> : <Minimize2 className="w-3 h-3 text-white" />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); returnToDock(); }} className="p-1 hover:bg-white/20 rounded transition-colors" title="Return to Dock">
            <X className="w-3 h-3 text-white" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <div className="bg-slate-800/95 backdrop-blur-sm">
          <div className="p-3 border-b border-slate-700 space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newTodoText}
                onChange={(e) => setNewTodoText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTodo(); } }}
                placeholder="Add a task..."
                className={`flex-1 bg-slate-700/50 text-white text-sm px-3 py-2 rounded-lg border border-slate-600 focus:outline-none ${isMobile ? 'focus:border-emerald-400' : 'focus:border-purple-400'} placeholder-slate-400`}
              />
              <button onClick={(e) => { e.stopPropagation(); addTodo(); }} className={`p-2 ${isMobile ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-purple-600 hover:bg-purple-700'} rounded-lg transition-colors`}>
                <Plus className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="date"
                value={newTodoDueDate}
                onChange={(e) => setNewTodoDueDate(e.target.value)}
                className={`flex-1 bg-slate-700/50 text-white text-xs px-2 py-1.5 rounded-lg border border-slate-600 focus:outline-none ${isMobile ? 'focus:border-emerald-400' : 'focus:border-purple-400'}`}
              />
              {newTodoDueDate && (
                <button onClick={() => setNewTodoDueDate('')} className="text-xs text-slate-400 hover:text-white">Clear</button>
              )}
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto p-3 space-y-2">
            {isLoading ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <div className={`animate-spin w-6 h-6 border-2 ${isMobile ? 'border-emerald-500' : 'border-purple-500'} border-t-transparent rounded-full mx-auto mb-2`}></div>
                <p>Loading tasks...</p>
              </div>
            ) : (
              <>
                {activeTodos.map(todo => {
                  const dueStatus = getDueStatus(todo.dueDate);
                  const DueIcon = dueStatus?.icon;
                  
                  return (
                    <div key={todo.id} className="flex flex-col gap-1 p-2 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors group">
                      <div className="flex items-start gap-2">
                        <input type="checkbox" checked={todo.completed} onChange={(e) => { e.stopPropagation(); toggleTodo(todo.id); }} className={`mt-1 w-4 h-4 rounded border-slate-500 ${isMobile ? 'text-emerald-600 focus:ring-emerald-500' : 'text-purple-600 focus:ring-purple-500'} cursor-pointer flex-shrink-0`} />
                        
                        {editingTextId === todo.id ? (
                          <input
                            type="text"
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); updateTodoText(todo.id, editingText); }
                              if (e.key === 'Escape') { setEditingTextId(null); }
                            }}
                            onBlur={() => { if (editingText.trim()) updateTodoText(todo.id, editingText); else setEditingTextId(null); }}
                            autoFocus
                            className={`flex-1 bg-slate-700 text-white text-sm px-2 py-1 rounded border ${isMobile ? 'border-emerald-500' : 'border-purple-500'} focus:outline-none`}
                          />
                        ) : (
                          <span 
                            className="flex-1 text-sm text-white break-words cursor-text"
                            onClick={() => { setEditingTextId(todo.id); setEditingText(todo.text); }}
                          >
                            {todo.text}
                          </span>
                        )}
                        
                        {!editingTextId && (
                          <>
                            <button onClick={(e) => { e.stopPropagation(); setEditingTextId(todo.id); setEditingText(todo.text); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-blue-500/20 rounded transition-all flex-shrink-0">
                              <Edit2 className="w-3 h-3 text-blue-400" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); deleteTodo(todo.id); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all flex-shrink-0">
                              <Trash2 className="w-3 h-3 text-red-400" />
                            </button>
                          </>
                        )}
                      </div>
                      
                      {editingDateId === todo.id ? (
                        <div className="flex items-center gap-2 ml-6">
                          <input
                            type="date"
                            defaultValue={todo.dueDate || ''}
                            onChange={(e) => updateTodoDueDate(todo.id, e.target.value)}
                            onBlur={() => setEditingDateId(null)}
                            autoFocus
                            className={`flex-1 bg-slate-700 text-white text-xs px-2 py-1 rounded border ${isMobile ? 'border-emerald-500' : 'border-purple-500'} focus:outline-none`}
                          />
                        </div>
                      ) : (
                        todo.dueDate ? (
                          <div className="flex items-center gap-2 ml-6">
                            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${dueStatus?.color}`}>
                              {DueIcon && <DueIcon className="w-3 h-3" />}
                              <span>{formatDueDate(todo.dueDate)}</span>
                            </div>
                            <button onClick={() => setEditingDateId(todo.id)} className="opacity-0 group-hover:opacity-100 text-xs text-slate-400 hover:text-white">
                              Edit
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setEditingDateId(todo.id)} className="opacity-0 group-hover:opacity-100 ml-6 text-xs text-slate-400 hover:text-purple-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Add due date
                          </button>
                        )
                      )}
                    </div>
                  );
                })}

                {completedTodos.length > 0 && (
                  <>
                    {activeTodos.length > 0 && <div className="border-t border-slate-700 my-2" />}
                    <div className="text-xs text-slate-400 font-semibold mb-2">Completed ({completedTodos.length})</div>
                    {completedTodos.map(todo => (
                      <div key={todo.id} className="flex items-start gap-2 p-2 bg-slate-700/20 rounded-lg hover:bg-slate-700/30 transition-colors group opacity-60">
                        <input type="checkbox" checked={todo.completed} onChange={(e) => { e.stopPropagation(); toggleTodo(todo.id); }} className={`mt-1 w-4 h-4 rounded border-slate-500 ${isMobile ? 'text-emerald-600 focus:ring-emerald-500' : 'text-purple-600 focus:ring-purple-500'} cursor-pointer flex-shrink-0`} />
                        <span className="flex-1 text-sm text-slate-400 break-words animate-strikethrough">
                          {todo.text}
                        </span>
                        <button onClick={(e) => { e.stopPropagation(); deleteTodo(todo.id); }} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all flex-shrink-0">
                          <Trash2 className="w-3 h-3 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </>
                )}

                {todos.length === 0 && !isLoading && (
                  <div className="text-center py-8 text-slate-400 text-sm">
                    <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No tasks yet!</p>
                    <p className="text-xs mt-1">Add your first task above</p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="px-3 py-2 border-t border-slate-700 text-[10px] text-slate-500 text-center">
            Press Cmd/Ctrl + Shift + T to toggle
          </div>
        </div>
      )}
    </div>
  );
}
